import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { capAncestors, documentNodeLabel } from '../../utils/documentTreePath';
import { openDocumentTab } from '../../store/tabsStore';
import { revealInSidebar } from './revealInSidebar';
import useScopeName from './useScopeName';

/**
 * `Scope › Folder › … › Title` (card 2.1). Every ancestor is a link that
 * reveals it in the sidebar (a document ancestor also opens its tab, as the
 * old breadcrumb did); the title segment is the current page and truncates.
 * Long paths elide their middle exactly like the command palette (capAncestors).
 */
export default function ToolbarBreadcrumb({ ancestors, scope, title }) {
  const { t } = useTranslation();
  const scopeName = useScopeName(scope);
  const { elided, items } = capAncestors(ancestors || []);

  const reveal = (node) => {
    const i = ancestors.findIndex((n) => n.id === node.id);
    const chain = ancestors.slice(0, i + 1).map((n) => n.id);
    if (node.type === 'document') {
      openDocumentTab({ documentId: node.id, label: node.name || t('docEditor.untitled'), scope });
    }
    revealInSidebar(scope, node.id, chain);
  };

  return (
    <nav className="doc-crumbs" aria-label={t('docEditor.toolbar.path')}>
      <button type="button" className="doc-crumb" onClick={() => revealInSidebar(scope, null)}>
        {scopeName}
      </button>
      {elided && (
        <>
          <ChevronRight size={14} strokeWidth={1.6} className="doc-crumb-sep" aria-hidden />
          <span className="doc-crumb-ellipsis" aria-hidden>
            …
          </span>
        </>
      )}
      {items.map((node) => (
        <React.Fragment key={node.id}>
          <ChevronRight size={14} strokeWidth={1.6} className="doc-crumb-sep" aria-hidden />
          <button type="button" className="doc-crumb" onClick={() => reveal(node)}>
            {documentNodeLabel(node)}
          </button>
        </React.Fragment>
      ))}
      <ChevronRight size={14} strokeWidth={1.6} className="doc-crumb-sep" aria-hidden />
      <span className="doc-crumb-current" aria-current="page">
        {title || t('docEditor.untitled')}
      </span>
    </nav>
  );
}
